import {
    DESIGNATION_PLACEHOLDER,
    SERVER_DESIGNATION_PLACEHOLDER,
} from '../constants'

/**
 * Checks if text is a valid designation element that should show the DesignationField.
 *
 * This function validates whether text contains a designation format that qualifies
 * for showing the drawing designation UI component.
 *
 * @param text - The text to validate
 * @returns true if the text has valid designation format, false otherwise
 *
 * ## Valid Formats:
 *
 * ### Case 1: Parentheses with non-placeholder content
 * Text with parentheses containing actual designation values.
 * @example
 * hasDesignationFormat("Glazed Wall Tile Type (ACME-US)") // true
 * hasDesignationFormat("Paint (SHERWIN-123):") // true
 *
 * ### Case 2: Parentheses with placeholder but meaningful prefix
 * Text with placeholder in parentheses but has a meaningful product type name before it.
 * @example
 * hasDesignationFormat("Glazed Wall Tile Type (Product Designation):") // true
 * hasDesignationFormat("Paint (Product Designation)") // true
 *
 * ### Case 3: Keyed format without parentheses yet
 * Traditional keyed text with colon but no designation parentheses.
 * @example
 * hasDesignationFormat("Glazed Wall Tile Type:") // true
 * hasDesignationFormat("Paint: Red") // true
 *
 * ## Invalid Formats:
 *
 * ### Placeholder only without meaningful prefix
 * Text that consists only of the placeholder without any product type context.
 * @example
 * hasDesignationFormat("(Product Designation)") // false
 * hasDesignationFormat("(Product Designation):") // false
 *
 * ### Empty or no format
 * @example
 * hasDesignationFormat("") // false
 * hasDesignationFormat("Simple text") // false
 */
export const hasDesignationFormatInText = (text: string) => {
    const openParen = text.indexOf('(')
    const closeParen = text.lastIndexOf(')')
    const colonIndex = text.indexOf(':')

    // Case 1: Has parentheses
    if (openParen !== -1 && closeParen !== -1 && closeParen > openParen + 1) {
        const contentInParens = text.substring(openParen + 1, closeParen).trim()
        const isPlaceholder =
            contentInParens.toLowerCase() ===
                DESIGNATION_PLACEHOLDER.toLowerCase() ||
            contentInParens.toLowerCase() ===
                SERVER_DESIGNATION_PLACEHOLDER.toLowerCase()

        if (!isPlaceholder) {
            return true
        }

        // If placeholder, check if there's meaningful prefix before parentheses
        const prefix = text.substring(0, openParen).trim()
        return prefix.length > 0
    }

    // Case 2: No parentheses but has colon (keyed text without designation yet)
    if (colonIndex !== -1) {
        const prefix = text.substring(0, colonIndex).trim()
        // Must have meaningful prefix (not empty and not just placeholder text)
        return (
            prefix.length > 0 &&
            prefix.toLowerCase() !== DESIGNATION_PLACEHOLDER.toLowerCase() &&
            prefix.toLowerCase() !==
                SERVER_DESIGNATION_PLACEHOLDER.toLowerCase()
        )
    }

    return false
}
